import { createClient, type SupabaseClient } from '@supabase/supabase-js'

import { getBridgeSupabaseAccessToken } from './bridge-auth'
import { appEnv } from './env'

/**
 * Cliente Supabase do Hub — schema dedicado `sistemas`.
 *
 * O JWT vem do bridge (produção) ou do login de desenvolvimento pré-SSO.
 * Anon key no browser; autorização real é JWT + RLS.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SistemasClient = SupabaseClient<any, 'sistemas', any>

let client: SistemasClient | null = null

/**
 * URL absoluta do Supabase a partir do que foi inlinado no build.
 *
 * Em produção o build usa `/supabase` — same-origin, proxiado pelo nginx do
 * app, para o mesmo bundle servir no dev01 e no dev02 e o IP do Kong nunca ir
 * para o navegador. Mas o `supabase-js` exige URL ABSOLUTA: por dentro ele faz
 * `new URL(url)`, e com `/supabase` isso lança "Invalid URL". Foi o que
 * derrubou o primeiro acesso em produção (2026-09-23), depois de o SSO já ter
 * autenticado. Resolver contra a origem da página mantém o bundle sem domínio
 * fixo; URL já absoluta (dev) passa intacta.
 */
export function resolveSupabaseUrl(raw: string, origin: string): string {
  return new URL(raw, origin).toString().replace(/\/$/, '')
}

export function getSupabase(): SistemasClient {
  if (!client) {
    client = createClient(resolveSupabaseUrl(appEnv.supabaseUrl, window.location.origin), appEnv.supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
      db: { schema: 'sistemas' },
      global: {
        fetch: (input, init = {}) => {
          const headers = new Headers(init.headers)
          const bridgeToken = getBridgeSupabaseAccessToken()
          // SOBRESCREVE sempre. O supabase-js já injeta `Authorization: Bearer
          // <anonKey>` em toda request, então um guard do tipo
          // `!headers.has('Authorization')` nunca dispararia e o token do
          // bridge jamais seria usado — o RLS veria `anon` e devolveria vazio
          // em tudo, com cara de "não tem dado" em vez de "não autenticado".
          if (bridgeToken) {
            headers.set('Authorization', `Bearer ${bridgeToken}`)
          }
          return fetch(input, { ...init, headers })
        },
      },
    }) as SistemasClient
  }
  return client
}

/** PostgREST/GoTrue respondem 401 quando o JWT expirou ou foi rejeitado. */
export function isUnauthorizedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const status = (error as { status?: number; code?: string; message?: string }).status
  const code = (error as { code?: string }).code
  const message = (error as { message?: string }).message ?? ''
  return (
    status === 401 ||
    code === 'PGRST301' ||
    /jwt|unauthorized|not authenticated/i.test(message)
  )
}

/**
 * O servidor ficou inalcançável — rede, VPN ou o `dev01` fora do ar.
 *
 * Distinguir isso de "não autorizado" e de "não existe" importa mais do que
 * parece: o proxy do Vite devolve **500** quando não alcança o Kong, e um 500
 * genérico manda o time procurar bug no banco enquanto o problema é o cabo.
 * Aconteceu, custou tempo, e virou este helper.
 */
export function isUnreachableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const status = (error as { status?: number }).status
  const message = (error as { message?: string }).message ?? ''
  return (
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504 ||
    status === 0 ||
    /failed to fetch|networkerror|load failed|etimedout|econnrefused|timeout/i.test(message)
  )
}

/**
 * Mensagem de erro de banco pronta para a tela.
 *
 * Toda leitura e escrita passa por aqui para que o usuário receba a causa, não
 * o sintoma: sem rede, sem sessão, ou a recusa real do Postgres (que costuma
 * ser uma regra de negócio falando — responsável ausente, valor congelado).
 */
export function describeDbError(error: unknown, fallback: string): string {
  if (isUnreachableError(error)) {
    return appEnv.authMode === 'local'
      ? 'Sem conexão com o Supabase local. Rode `pnpm db:start` e tente de novo.'
      : 'Sem conexão com o banco do departamento. Verifique a rede ou a VPN e tente de novo.'
  }
  if (isUnauthorizedError(error)) {
    return 'Sua sessão expirou ou não autoriza esta operação. Recarregue a página para entrar de novo.'
  }
  const message = (error as { message?: string } | null)?.message?.trim()
  return message || fallback
}
